import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';

import { formatPercent, formatPointChange } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { changeRoundsToZero, type ChangeKind } from '@/lib/period-change';

/** `percent`: a fraction (0.12 is +12%). `points`: percentage points (1.2 is +1.2 pp). */
export type TrendChangeKind = ChangeKind;

const SIZE = {
  /** 14px, beside a KPI value or in a table cell. */
  default: { text: 'text-sm', icon: 'size-3.5' },
  /** 12px, beside a smaller value (dense tiles). */
  sm: { text: 'text-xs', icon: 'size-3' },
} as const;

export type TrendIndicatorSize = keyof typeof SIZE;

interface SignedChangeProps {
  /**
   * The change against the earlier period, signed: a fraction for `percent`, points for `points`.
   * Null, undefined or NaN shows nothing. Work it out with `changeFrom` / `pointsFrom` from
   * `@/lib/period-change`.
   */
  change: number | null | undefined;
  /** @default 'percent' */
  kind?: TrendChangeKind;
  /** Whether a rise is good news (false for costs). Sets the colour; the arrow and sign follow the number. @default true */
  upIsGood?: boolean;
  /** What it's compared with, for screen readers: "year over year", "vs the 30 days before". */
  label?: string;
  /** @default 'default' */
  size?: TrendIndicatorSize;
  className?: string;
}

interface ExplicitProps {
  /** The direction of the trend ('upward' or 'downward') */
  direction: 'upward' | 'downward';
  /** Whether the change is good news ('positive') or bad ('negative') */
  trend: 'positive' | 'negative';
  /** The value to display, already formatted (e.g. "10") */
  value: string;
  /** Accessibility label for screen readers */
  accessibilityLabel: string;
  /**
   * Whether the value already includes a % sign
   * @default false
   */
  valueIncludesPercentSign?: boolean;
  /** Applied to the value (overrides the default `text-sm`). */
  className?: string;
}

export type TrendIndicatorProps = SignedChangeProps | ExplicitProps;

/**
 * A change against an earlier period: an arrow, the signed change and a colour for good or bad news
 * (not colour alone: the arrow and sign carry the direction).
 *
 * Pass `change` (a signed number) and the indicator works out the arrow, the sign, the colour and the
 * format (`formatPercent(…, 'change')` or `formatPointChange`); a change that rounds to zero shows
 * grey with no arrow. The explicit form (`direction`, `trend`, a formatted `value`) is for a table
 * cell whose change is in its own unit (a money or count change).
 */
export function TrendIndicator(props: TrendIndicatorProps) {
  if ('change' in props) return <SignedChange {...props} />;
  return <ExplicitTrend {...props} />;
}

function SignedChange({
  change,
  kind = 'percent',
  upIsGood = true,
  label,
  size = 'default',
  className,
}: SignedChangeProps) {
  if (change == null || !Number.isFinite(change)) return null;
  const flat = changeRoundsToZero(change, kind);
  const up = change > 0;
  const Icon = up ? ArrowUpIcon : ArrowDownIcon;
  const text = kind === 'points' ? formatPointChange(change) : formatPercent(change, 'change');
  const s = SIZE[size];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 font-medium whitespace-nowrap tabular-nums',
        s.text,
        flat ? 'text-muted-foreground' : up === upIsGood ? 'text-success' : 'text-danger',
        className
      )}
    >
      {!flat && <Icon className={cn('shrink-0', s.icon)} aria-hidden />}
      {text}
      {label && <span className="sr-only"> {label}</span>}
    </span>
  );
}

function ExplicitTrend({
  direction,
  trend,
  value,
  accessibilityLabel,
  valueIncludesPercentSign = false,
  className,
}: ExplicitProps) {
  const Icon = direction === 'upward' ? ArrowUpIcon : ArrowDownIcon;
  const displayValue = valueIncludesPercentSign ? value : `${value}%`;
  const toneClass = trend === 'positive' ? 'text-success' : 'text-danger';

  return (
    <div
      className={cn('inline-flex items-center gap-0.5', toneClass)}
      aria-label={accessibilityLabel}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden />
      <span className={cn('text-sm font-medium', className)}>{displayValue}</span>
    </div>
  );
}
